import { ACTIONS, MODULES } from 'constants/permissions';
import { ROUTES } from 'constants/routes';

/**
 * Permission helpers operating on the nested permission object stored on the
 * authenticated user (loaded directly from the backend's login response):
 *   user.permissions = { [module]: { [action]: boolean } }
 */

export const getPermissions = (user) => user?.permissions || {};

/**
 * The backend's built-in Super Administrator: flagged `is_system_admin` or
 * `bypass_permissions` on login, with no `permissions` object needed at all.
 * This user bypasses RBAC entirely — every permission check below returns
 * true for them without evaluating module permissions, and the sidebar (see
 * Sidebar.js) shows the full static navigation as a result.
 */
export const isAdminBypass = (user) => Boolean(user?.isSystemAdmin || user?.bypassPermissions);

/** True if the user can perform `action` on `module`. */
export const hasPermission = (user, module, action = ACTIONS.VIEW) => {
  if (!module) return true;
  if (isAdminBypass(user)) return true;
  const perms = getPermissions(user);
  return Boolean(perms[module] && perms[module][action]);
};

/** True if the user can view (access) the module at all. */
export const canAccessModule = (user, module) => hasPermission(user, module, ACTIONS.VIEW);

/** True if the user has ANY action enabled within a module. */
export const hasAnyModulePermission = (user, module) => {
  if (isAdminBypass(user)) return true;
  const perms = getPermissions(user);
  return Boolean(perms[module] && Object.values(perms[module]).some(Boolean));
};

/** Returns the first available route for a user based on their permissions. */
export const getDefaultRoute = (user) => {
  if (isAdminBypass(user)) return ROUTES.DASHBOARD;
  const perms = getPermissions(user);
  
  if (perms[MODULES.DASHBOARD]?.view) return ROUTES.DASHBOARD;
  if (perms[MODULES.STAGE1]?.view) return ROUTES.STAGE1;
  if (perms[MODULES.STAGE2]?.view) return ROUTES.STAGE2;
  if (perms[MODULES.BRIEFING]?.view) return ROUTES.BRIEFING;
  if (perms[MODULES.STORAGE1]?.view) return ROUTES.STORAGE1;
  if (perms[MODULES.STORAGE2]?.view) return ROUTES.STORAGE2;
  if (perms[MODULES.OCR_STORAGE]?.view) return ROUTES.OCR_STORAGE;
  if (perms[MODULES.NO_STORAGE]?.view) return ROUTES.NO_STORAGE;
  if (perms[MODULES.SEARCH]?.view) return ROUTES.SEARCH;
  
  // Settings fallback
  if (perms[MODULES.NEWSPAPERS]?.view) return ROUTES.SETTINGS_NEWSPAPERS;
  if (perms[MODULES.ROLES]?.view) return ROUTES.SETTINGS_ROLES;
  if (perms[MODULES.USERS]?.view) return ROUTES.SETTINGS_USERS;
  if (perms[MODULES.SYSTEM_CONFIG]?.view) return ROUTES.SETTINGS_SYSTEM_CONFIG;
  if (perms[MODULES.ACTIVITY_LOGS]?.view) return ROUTES.SETTINGS_ACTIVITY_LOGS;

  return ROUTES.FORBIDDEN;
};

const permissionUtils = {
  getPermissions,
  isAdminBypass,
  hasPermission,
  canAccessModule,
  hasAnyModulePermission,
  getDefaultRoute,
};

export default permissionUtils;
